import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { isToday } from 'date-fns';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useBoolean } from 'usehooks-ts';

import { DatePicker } from '@/components/form/inputs/date-picker';
import { useMe } from '@/hooks/use-me';
import { cls } from '@/utils/cls';
import { singular } from '@/utils/format/label';

import type { Asset } from '../entities/asset';
import { AssetType } from '../entities/asset-type';
import { ViewFiltersMode } from '../entities/view-filters';
import { useAssetTypesExist } from '../hooks/use-asset-types-exist';
import { useAssetsFilters } from '../hooks/use-assets-filters';
import { useViewFilters } from '../hooks/use-view-filters';
import {
  ACCURE_SOC_RANGE,
  BMS_SOC_RANGE,
  IMBALANCE_SOC_RANGE,
  TEMPERATURE_RANGE,
} from '../providers/view-filters';
import { GetAllAssets } from '../queries/get-all-assets';

import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Slider } from './ui/slider';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';

export type FiltersProps = {};

const labelFromViewMode: Record<ViewFiltersMode, string> = {
  [ViewFiltersMode.REALISTIC]: 'Realistic View',
  [ViewFiltersMode.ACCURE_SOC]: 'Accure SOC',
  [ViewFiltersMode.BMS_SOC]: 'BMS SOC',
  [ViewFiltersMode.SOC_OFFSET]: 'SOC Offset',
  [ViewFiltersMode.ALERT_STATUS]: 'Alert Status',
  [ViewFiltersMode.IMBALANCE_SOC]: 'Imbalance SOC',
  [ViewFiltersMode.TEMPERATURE]: 'Temperature',
};

const viewModesPerAssetType: Record<AssetType, ViewFiltersMode[]> = {
  [AssetType.CONTAINER]: [
    ViewFiltersMode.REALISTIC,
    ViewFiltersMode.ALERT_STATUS,
    ViewFiltersMode.IMBALANCE_SOC,
    ViewFiltersMode.SOC_OFFSET,
    ViewFiltersMode.ACCURE_SOC,
    ViewFiltersMode.BMS_SOC,
    ViewFiltersMode.TEMPERATURE,
  ],
  [AssetType.PCS]: [
    ViewFiltersMode.ALERT_STATUS,
    ViewFiltersMode.IMBALANCE_SOC,
    ViewFiltersMode.SOC_OFFSET,
    ViewFiltersMode.ACCURE_SOC,
    ViewFiltersMode.BMS_SOC,
    ViewFiltersMode.TEMPERATURE,
  ],
  [AssetType.STRING]: [
    ViewFiltersMode.ALERT_STATUS,
    ViewFiltersMode.IMBALANCE_SOC,
    ViewFiltersMode.SOC_OFFSET,
    ViewFiltersMode.ACCURE_SOC,
    ViewFiltersMode.BMS_SOC,
    ViewFiltersMode.TEMPERATURE,
  ],
  [AssetType.RACK]: [
    ViewFiltersMode.ALERT_STATUS,
    ViewFiltersMode.IMBALANCE_SOC,
    ViewFiltersMode.SOC_OFFSET,
    ViewFiltersMode.ACCURE_SOC,
    ViewFiltersMode.BMS_SOC,
    ViewFiltersMode.TEMPERATURE,
  ],
  [AssetType.MODULE]: [
    ViewFiltersMode.ALERT_STATUS,
    ViewFiltersMode.IMBALANCE_SOC,
    ViewFiltersMode.SOC_OFFSET,
    ViewFiltersMode.ACCURE_SOC,
    ViewFiltersMode.BMS_SOC,
    ViewFiltersMode.TEMPERATURE,
  ],
};

const viewModesActive: Record<ViewFiltersMode, (asset: Asset) => boolean> = {
  [ViewFiltersMode.REALISTIC]: () => true,
  [ViewFiltersMode.ALERT_STATUS]: () => true,
  [ViewFiltersMode.ACCURE_SOC]: (asset) => typeof asset.accureSoc === 'number',
  [ViewFiltersMode.BMS_SOC]: (asset) => typeof asset.bmsSoc === 'number',
  [ViewFiltersMode.SOC_OFFSET]: (asset) =>
    typeof asset.accureSoc === 'number' && typeof asset.bmsSoc === 'number',
  [ViewFiltersMode.IMBALANCE_SOC]: (asset) =>
    typeof asset.imbalanceSoc === 'number',
  [ViewFiltersMode.TEMPERATURE]: (asset) =>
    typeof asset.temperature === 'number',
};

export function Filters(_: FiltersProps) {
  const showFilters = useBoolean(true);
  const [assetsFilters, setAssetsFilters] = useAssetsFilters();
  const [viewFilters, setViewFilters] = useViewFilters();
  const assetTypesExist = useAssetTypesExist();
  const { siteId } = useParams<{ siteId: string }>();
  const allAssetsQuery = GetAllAssets.useQuery({ siteId, assetsFilters });

  useEffect(
    function () {
      if (!allAssetsQuery.data?.assetsFilters) {
        return;
      }

      const viewModes =
        viewModesPerAssetType[allAssetsQuery.data.assetsFilters.assetType];

      if (
        viewModes &&
        viewModes.length > 0 &&
        !viewModes.includes(viewFilters.viewMode)
      ) {
        setViewFilters((viewFilters) => ({
          ...viewFilters,
          viewMode: viewModes[0],
        }));
      }
    },
    [allAssetsQuery.data?.assetsFilters, viewFilters.viewMode, setViewFilters],
  );

  const availableViewModels = useMemo<ViewFiltersMode[]>(
    () =>
      viewModesPerAssetType[assetsFilters.assetType].filter((viewMode) =>
        allAssetsQuery.data?.assets.some((asset) =>
          viewModesActive[viewMode](asset),
        ),
      ),
    [allAssetsQuery.data?.assets, assetsFilters.assetType],
  );

  return (
    <div className="w-full space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
      <div
        role="button"
        className="flex size-full items-center justify-between"
        onClick={showFilters.toggle}
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="size-4 text-gray-600" />
          <div className="text-base font-semibold">Filters</div>
        </div>
        <Button variant="ghost" size="sm" className="size-8 p-0">
          <ChevronDown
            className={cls('size-4 text-gray-500 transition-transform', {
              'rotate-180': showFilters.value,
            })}
          />
        </Button>
      </div>
      {showFilters.value ? (
        <div className="space-y-4">
          <DatePicker
            value={assetsFilters.date}
            disabledDates={{ after: new Date() }}
            hint={
              isToday(assetsFilters.date)
                ? 'Analytics for today may not have completed yet'
                : undefined
            }
            onSelect={function (date) {
              if (date) {
                setAssetsFilters((assetsFilters) => ({
                  ...assetsFilters,
                  date,
                }));
              }
            }}
          />

          <Tabs
            defaultValue={assetsFilters.assetType}
            onValueChange={(value) =>
              setAssetsFilters((assetsFilters) => ({
                ...assetsFilters,
                assetType: value as AssetType,
              }))
            }
            className="w-full"
          >
            <TabsList className="flex w-full gap-2 bg-transparent p-0">
              {assetTypesExist[AssetType.CONTAINER] ? (
                <AssetTypeTabsItem assetType={AssetType.CONTAINER} />
              ) : null}
              {assetTypesExist[AssetType.STRING] ? (
                <AssetTypeTabsItem assetType={AssetType.STRING} />
              ) : null}
              {assetTypesExist[AssetType.RACK] ? (
                <AssetTypeTabsItem assetType={AssetType.RACK} />
              ) : null}
              {assetTypesExist[AssetType.MODULE] ? (
                <AssetTypeTabsItem assetType={AssetType.MODULE} />
              ) : null}
            </TabsList>
          </Tabs>

          <Separator />

          <div className="space-y-3">
            <Label className="text-sm font-medium">Point of view</Label>
            <Select
              onValueChange={(viewMode) =>
                setViewFilters((viewFilters) => ({
                  ...viewFilters,
                  viewMode: viewMode as ViewFiltersMode,
                }))
              }
              value={viewFilters.viewMode}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a view" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {availableViewModels.map((v) => (
                    <SelectItem key={v} value={v}>
                      {labelFromViewMode[v]}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {viewFilters.viewMode === ViewFiltersMode.ACCURE_SOC ? (
            <div>
              <Label className="mb-1 block text-sm font-medium text-gray-700">
                ACCURE SOC
              </Label>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Slider
                    className="relative flex h-5 w-full touch-none select-none items-center"
                    value={[
                      viewFilters.accureSoc.min,
                      viewFilters.accureSoc.max,
                    ]}
                    onValueChange={([min, max]) =>
                      setViewFilters((viewFilters) => ({
                        ...viewFilters,
                        accureSoc: {
                          min,
                          max,
                        },
                      }))
                    }
                    min={ACCURE_SOC_RANGE.min}
                    max={ACCURE_SOC_RANGE.max}
                    step={0.01}
                    minStepsBetweenThumbs={0}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-24">
                    <Label
                      htmlFor="accure-min"
                      className="mb-1 block text-xs text-gray-500"
                    >
                      Min
                    </Label>
                    <div className="relative">
                      <input
                        id="accure-min"
                        type="number"
                        value={Math.floor(viewFilters.accureSoc.min * 100)}
                        onChange={(e) =>
                          setViewFilters((viewFilters) => ({
                            ...viewFilters,
                            accureSoc: {
                              ...viewFilters.accureSoc,
                              min: Math.floor(Number(e.target.value)) / 100,
                            },
                          }))
                        }
                        min={Math.floor(ACCURE_SOC_RANGE.min * 100)}
                        max={Math.floor(viewFilters.accureSoc.max * 100)}
                        step={1}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm [appearance:textfield] focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        %
                      </span>
                    </div>
                  </div>
                  <div className="w-24">
                    <Label
                      htmlFor="accure-max"
                      className="mb-1 block text-xs text-gray-500"
                    >
                      Max
                    </Label>
                    <div className="relative">
                      <input
                        id="accure-max"
                        type="number"
                        value={Math.floor(viewFilters.accureSoc.max * 100)}
                        onChange={(e) =>
                          setViewFilters((viewFilters) => ({
                            ...viewFilters,
                            accureSoc: {
                              ...viewFilters.accureSoc,
                              max: Math.floor(Number(e.target.value)) / 100,
                            },
                          }))
                        }
                        min={Math.floor(viewFilters.accureSoc.min * 100)}
                        max={Math.floor(ACCURE_SOC_RANGE.max * 100)}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm [appearance:textfield] focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {viewFilters.viewMode === ViewFiltersMode.BMS_SOC ? (
            <div>
              <Label className="mb-1 block text-sm font-medium text-gray-700">
                BMS SOC
              </Label>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Slider
                    className="relative flex h-5 w-full touch-none select-none items-center"
                    value={[viewFilters.bmsSoc.min, viewFilters.bmsSoc.max]}
                    onValueChange={([min, max]) =>
                      setViewFilters((viewFilters) => ({
                        ...viewFilters,
                        bmsSoc: {
                          min,
                          max,
                        },
                      }))
                    }
                    min={BMS_SOC_RANGE.min}
                    max={BMS_SOC_RANGE.max}
                    step={0.01}
                    minStepsBetweenThumbs={0}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-24">
                    <Label
                      htmlFor="bms-min"
                      className="mb-1 block text-xs text-gray-500"
                    >
                      Min
                    </Label>
                    <div className="relative">
                      <input
                        id="bms-min"
                        type="number"
                        value={Math.floor(viewFilters.bmsSoc.min * 100)}
                        onChange={(e) =>
                          setViewFilters((viewFilters) => ({
                            ...viewFilters,
                            bmsSoc: {
                              ...viewFilters.bmsSoc,
                              min: Math.floor(Number(e.target.value)) / 100,
                            },
                          }))
                        }
                        min={Math.floor(BMS_SOC_RANGE.min * 100)}
                        max={Math.floor(viewFilters.bmsSoc.max * 100)}
                        step={1}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm [appearance:textfield] focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        %
                      </span>
                    </div>
                  </div>
                  <div className="w-24">
                    <Label
                      htmlFor="bms-max"
                      className="mb-1 block text-xs text-gray-500"
                    >
                      Max
                    </Label>
                    <div className="relative">
                      <input
                        id="bms-max"
                        type="number"
                        value={Math.floor(viewFilters.bmsSoc.max * 100)}
                        onChange={(e) =>
                          setViewFilters((viewFilters) => ({
                            ...viewFilters,
                            bmsSoc: {
                              ...viewFilters.bmsSoc,
                              max: Math.floor(Number(e.target.value)) / 100,
                            },
                          }))
                        }
                        min={Math.floor(viewFilters.bmsSoc.min * 100)}
                        max={Math.floor(BMS_SOC_RANGE.max * 100)}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm [appearance:textfield] focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {viewFilters.viewMode === ViewFiltersMode.IMBALANCE_SOC ? (
            <div>
              <Label className="mb-1 block text-sm font-medium text-gray-700">
                Imbalance SOC
              </Label>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Slider
                    className="relative flex h-5 w-full touch-none select-none items-center"
                    value={[
                      viewFilters.imbalanceSoc.min,
                      viewFilters.imbalanceSoc.max,
                    ]}
                    onValueChange={([min, max]) =>
                      setViewFilters((viewFilters) => ({
                        ...viewFilters,
                        imbalanceSoc: {
                          min,
                          max,
                        },
                      }))
                    }
                    min={IMBALANCE_SOC_RANGE.min}
                    max={IMBALANCE_SOC_RANGE.max}
                    step={0.01}
                    minStepsBetweenThumbs={0}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-24">
                    <Label
                      htmlFor="imbalance-min"
                      className="mb-1 block text-xs text-gray-500"
                    >
                      Min
                    </Label>
                    <div className="relative">
                      <input
                        id="imbalance-min"
                        type="number"
                        value={Math.floor(viewFilters.imbalanceSoc.min * 100)}
                        onChange={(e) =>
                          setViewFilters((viewFilters) => ({
                            ...viewFilters,
                            imbalanceSoc: {
                              ...viewFilters.imbalanceSoc,
                              min: Math.floor(Number(e.target.value)) / 100,
                            },
                          }))
                        }
                        min={Math.floor(IMBALANCE_SOC_RANGE.min * 100)}
                        max={Math.floor(viewFilters.imbalanceSoc.max * 100)}
                        step={1}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm [appearance:textfield] focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        %
                      </span>
                    </div>
                  </div>
                  <div className="w-24">
                    <Label
                      htmlFor="imbalance-max"
                      className="mb-1 block text-xs text-gray-500"
                    >
                      Max
                    </Label>
                    <div className="relative">
                      <input
                        id="imbalance-max"
                        type="number"
                        value={Math.floor(viewFilters.imbalanceSoc.max * 100)}
                        onChange={(e) =>
                          setViewFilters((viewFilters) => ({
                            ...viewFilters,
                            imbalanceSoc: {
                              ...viewFilters.imbalanceSoc,
                              max: Math.floor(Number(e.target.value)) / 100,
                            },
                          }))
                        }
                        min={Math.floor(viewFilters.imbalanceSoc.min * 100)}
                        max={Math.floor(IMBALANCE_SOC_RANGE.max * 100)}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm [appearance:textfield] focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {viewFilters.viewMode === ViewFiltersMode.TEMPERATURE ? (
            <div>
              <Label className="mb-1 block text-sm font-medium text-gray-700">
                Temperature
              </Label>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Slider
                    className="relative flex h-5 w-full touch-none select-none items-center"
                    value={[
                      viewFilters.temperature.min,
                      viewFilters.temperature.max,
                    ]}
                    onValueChange={([min, max]) =>
                      setViewFilters((viewFilters) => ({
                        ...viewFilters,
                        temperature: {
                          min,
                          max,
                        },
                      }))
                    }
                    min={TEMPERATURE_RANGE.min}
                    max={TEMPERATURE_RANGE.max}
                    step={1}
                    minStepsBetweenThumbs={0}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-24">
                    <Label
                      htmlFor="temperature-min"
                      className="mb-1 block text-xs text-gray-500"
                    >
                      Min
                    </Label>
                    <div className="relative">
                      <input
                        id="temperature-min"
                        type="number"
                        value={Math.round(viewFilters.temperature.min)}
                        onChange={(e) =>
                          setViewFilters((viewFilters) => ({
                            ...viewFilters,
                            temperature: {
                              ...viewFilters.temperature,
                              min: Number(e.target.value),
                            },
                          }))
                        }
                        min={TEMPERATURE_RANGE.min}
                        max={viewFilters.temperature.max}
                        step={1}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm [appearance:textfield] focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        °C
                      </span>
                    </div>
                  </div>
                  <div className="w-24">
                    <Label
                      htmlFor="temperature-max"
                      className="mb-1 block text-xs text-gray-500"
                    >
                      Max
                    </Label>
                    <div className="relative">
                      <input
                        id="temperature-max"
                        type="number"
                        value={Math.round(viewFilters.temperature.max)}
                        onChange={(e) =>
                          setViewFilters((viewFilters) => ({
                            ...viewFilters,
                            temperature: {
                              ...viewFilters.temperature,
                              max: Number(e.target.value),
                            },
                          }))
                        }
                        min={viewFilters.temperature.min}
                        max={TEMPERATURE_RANGE.max}
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm [appearance:textfield] focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        °C
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

type AssetTypeTabsItemProps = {
  assetType: AssetType;
};

function AssetTypeTabsItem({ assetType }: AssetTypeTabsItemProps) {
  const me = useMe();

  const labelFromAssetType: Record<AssetType, string> = {
    [AssetType.CONTAINER]: singular(me.config.top.labels.name),
    [AssetType.PCS]: 'PCS',
    [AssetType.STRING]: 'String',
    [AssetType.RACK]: singular(me.config.unit.labels.name),
    [AssetType.MODULE]: 'Module',
  };

  return (
    <TabsTrigger
      value={assetType}
      className="font-medium data-[state=active]:rounded-md data-[state=active]:border-primary-600 data-[state=active]:bg-primary-600 data-[state=active]:text-white"
    >
      <span className="text-base">{labelFromAssetType[assetType]}</span>
    </TabsTrigger>
  );
}
